import { FC, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";

import { Accordion, AccordionContainer } from "@app/shared";
import { Waffles } from "@app/shared/waffles";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { getComparisonChangeElement } from "@app/shared/utility";

import ProviderSetService from "@app/provider-sets/providerSetService";
import { ProviderSetVersionSummaryType } from "@app/provider-sets/enumerations";
import { ProviderSummaryCountItemModel, ProviderSetVersionSummaryCountModel } from "@app/provider-sets/providerSet";
import { ProviderSetSummaryCountTableData, ProviderSetSummaryDetailProps, createConfig, customSort } from "./ProviderSetDetailHelpers";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

export const SummaryInfo: FC<React.PropsWithChildren<ProviderSetSummaryDetailProps>> = (props: ProviderSetSummaryDetailProps) => {

  const providerSetService = new ProviderSetService();

  const [loading, setLoading] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState<ProviderSetVersionSummaryType>(props.activeAccordion);
  const [providersBySpecialty, setProvidersBySpecialty] = useState<ProviderSetVersionSummaryCountModel>();
  const [providersBySpecialtyCompare, setProvidersBySpecialtyCompare] = useState<ProviderSetVersionSummaryCountModel>();
  const [providersByState, setProvidersByState] = useState<ProviderSetVersionSummaryCountModel>();
  const [providersByStateCompare, setProvidersByStateCompare] = useState<ProviderSetVersionSummaryCountModel>();
  const [showAllStates, setShowAllStates] = useState(false);
  const [providersByGeocodeResult, setProvidersByGeocodeResult] = useState<ProviderSetVersionSummaryCountModel>();
  const [providersByGeocodeResultCompare, setProvidersByGeocodeResultCompare] = useState<ProviderSetVersionSummaryCountModel>();
  const filesOutlineIcon = <QesIcon isDecorative iconName={QesIcons.FontAwesome.FilesOutline} width="14" classNames="mr-1" svgStyle={{ bottom: "1px", position: "relative" }} />;

  useEffect(() => {
    setActiveAccordion(ProviderSetVersionSummaryType.Unknown);
  }, [props.providerSetId]);

  useEffect(() => {
    if (!!activeAccordion && activeAccordion !== ProviderSetVersionSummaryType.Unknown)
      fetchSummaryCount(activeAccordion);
  }, [props.version, props.compareVersion]);

  useEffect(() => {
    props.onUpdate(activeAccordion);
  }, [activeAccordion])


  const createSummaryCountWaffleTable = (summaryType: ProviderSetVersionSummaryType, data: ProviderSummaryCountItemModel[]): ProviderSetSummaryCountTableData[] => {
    return data.map<ProviderSetSummaryCountTableData>(x => {
      let count = (<>{x.totalUniqueRecordCount.toLocaleString("en-US")}</>);

      if (!!x.key && x.totalUniqueRecordCount > 0)
        count = (
          <Link to={{ pathname: `/provider-sets/${props.providerSetId}/version/${props.version.providerSetVersionId}/providers/${summaryType}/${x.key}` }} >{x.totalUniqueRecordCount.toLocaleString("en-US")}</Link>
        );

      /* eslint-disable sort-keys */
      return ({
        key: x.key,
        description: (!!x.displayName ? x.displayName : "<Not Provided>"),
        count: count,
        countNumeric: x.totalUniqueRecordCount,
        versionCount: null,
        compareVersionCount: null,
        change: null,
        changePerc: null,
      });
      /* eslint-enable sort-keys */
    });
  }

  const createSummaryCountCompareWaffleTable = (summaryType: ProviderSetVersionSummaryType, versionData: ProviderSummaryCountItemModel[], compareVersionData: ProviderSummaryCountItemModel[]): ProviderSetSummaryCountTableData[] => {
    const data = versionData.map(x => {
      return ({
        change: 0,
        changePerc: 0,
        compareVersionCount: 0,
        description: (!!x.displayName ? x.displayName : "<Not Provided>"),
        key: x.key,
        versionCount: x.totalUniqueRecordCount ?? 0,
      })
    });

    for (let i = 0; i < compareVersionData.length; i++) {
      const matchIndex = data.findIndex(x => x.key === compareVersionData[i].key);

      if (matchIndex > -1) {
        data[matchIndex].compareVersionCount = compareVersionData[i].totalUniqueRecordCount ?? 0;
      } else {
        data.push({
          change: 0,
          changePerc: 0,
          compareVersionCount: (compareVersionData[i].totalUniqueRecordCount ?? 0),
          description: (!!compareVersionData[i].displayName ? compareVersionData[i].displayName : "<Not Provided>"),
          key: compareVersionData[i].key,
          versionCount: 0,
        })
      }
    }

    return data.map<ProviderSetSummaryCountTableData>(x => {
      let versionCount = (<>{x.versionCount.toLocaleString("en-US")}</>);
      let compareVersionCount = (<>{x.compareVersionCount.toLocaleString("en-US")}</>);

      if (!!x.key && x.versionCount > 0)
        versionCount = (
          <Link to={{ pathname: `/provider-sets/${props.providerSetId}/version/${props.version.providerSetVersionId}/providers/${summaryType}/${x.key}` }} >{x.versionCount.toLocaleString("en-US")}</Link>
        );

      if (!!x.key && x.compareVersionCount > 0)
        compareVersionCount = (
          <Link to={{ pathname: `/provider-sets/${props.providerSetId}/version/${props.compareVersion.providerSetVersionId}/providers/${summaryType}/${x.key}` }} >{x.compareVersionCount.toLocaleString("en-US")}</Link>
        );

      const changeNum = x.versionCount - x.compareVersionCount;
      const changeDisp = getComparisonChangeElement(changeNum);

      let changePercNum = 0;
      if (x.compareVersionCount === 0) {
        if (x.versionCount > 0) {
          changePercNum = 100;
        } else {
          changePercNum = 0;
        }
      } else {
        //toFixed accurately rounds the result to 2 decimal places
        //parseFloat removes any trailing 0s
        changePercNum = parseFloat((changeNum / x.compareVersionCount * 100).toFixed(2));
      }
      const changePercDisp = getComparisonChangeElement(changePercNum, (num: number) => num + "%");

      /* eslint-disable sort-keys */
      return ({
        key: x.key,
        description: x.description,
        count: null,
        compareVersionCount: compareVersionCount,
        compareVersionCountNumeric: x.compareVersionCount,
        versionCount: versionCount,
        versionCountNumeric: x.versionCount,
        change: changeDisp,
        changeNumeric: changeNum,
        changePerc: changePercDisp,
        changePercNumeric: changePercNum
      });
      /* eslint-enable sort-keys */
    });
  }

  const providersBySpecialtyBody = useMemo(() => {
    const columnConfig = createConfig("Specialty", props);

    let waffleData: ProviderSetSummaryCountTableData[] = [];
    if (!!providersBySpecialty && !!props.compareVersion && !!providersBySpecialtyCompare)
      waffleData = createSummaryCountCompareWaffleTable(ProviderSetVersionSummaryType.BySpecCode, providersBySpecialty.summaryCount, providersBySpecialtyCompare.summaryCount);
    else if (!!providersBySpecialty)
      waffleData = createSummaryCountWaffleTable(ProviderSetVersionSummaryType.BySpecCode, providersBySpecialty.summaryCount);

    return (
      <Waffles
        columns={columnConfig}
        data={waffleData}
        customSort={customSort}
        fullWidth={true}
        loading={loading}
        testId="provider-set-detail-by-specialty-waffles-table"
      />);
  }, [providersBySpecialty, providersBySpecialtyCompare, loading]);

  const providersByStateBody = useMemo(() => {
    const columnConfig = createConfig("State", props);

    let waffleData: ProviderSetSummaryCountTableData[] = [];
    if (!!providersByState) {
      let tempProviders = providersByState.summaryCount;
      let tempProvidersCompare = !!providersByStateCompare ? providersByStateCompare.summaryCount : [];
      if (!showAllStates) {
        tempProviders = tempProviders.filter(x => x.totalUniqueRecordCount > 0);
        tempProvidersCompare = tempProvidersCompare.filter(x => x.totalUniqueRecordCount > 0)
      }

      if (!!providersByState && !!props.compareVersion && !!providersByStateCompare)
        waffleData = createSummaryCountCompareWaffleTable(ProviderSetVersionSummaryType.ByState, tempProviders, tempProvidersCompare);
      else if (!!providersByState)
        waffleData = createSummaryCountWaffleTable(ProviderSetVersionSummaryType.ByState, tempProviders);

    }

    const maxRecordsPerGrid = 28;
    return (
      <div className="row">
        <div className="col-sm-6 col-xs-12">
          <Waffles
            columns={columnConfig}
            customSort={customSort}
            data={waffleData.slice(0, maxRecordsPerGrid)}
            fullWidth={true}
            loading={loading}
            testId="provider-set-detail-by-state1-waffles-table"
          />
        </div>
        {waffleData.length > 27 ?
          (<div className="col-sm-6 col-xs-12">
            <Waffles
              columns={columnConfig}
              customSort={customSort}
              data={waffleData.slice(maxRecordsPerGrid)}
              fullWidth={true}
              loading={loading}
              testId="provider-set-detail-by-state2-waffles-table"
            />
          </div>) : null
        }
      </div>
    );
  }, [showAllStates, providersByState, loading]);

  const providersByGeocodeResultBody = useMemo(() => {
    const columnConfig = createConfig("Geocode Result", props);

    let waffleData: ProviderSetSummaryCountTableData[] = [];
    if (!!providersByGeocodeResult && !!props.compareVersion && !!providersByGeocodeResultCompare)
      waffleData = createSummaryCountCompareWaffleTable(ProviderSetVersionSummaryType.ByState, providersByGeocodeResult.summaryCount, providersByGeocodeResultCompare.summaryCount);
    else if (!!providersByGeocodeResult)
      waffleData = createSummaryCountWaffleTable(ProviderSetVersionSummaryType.ByGeocode, providersByGeocodeResult.summaryCount);

    return (
      <Waffles
        columns={columnConfig}
        customSort={customSort}
        data={waffleData}
        fullWidth={true}
        loading={loading}
        testId="provider-detail-by-geocode-result-waffles-table"
      />
    );
  }, [providersByGeocodeResult, loading]);

  const fetchSummaryCount = async (summaryType: ProviderSetVersionSummaryType) => {
    setLoading(true);

    let setProviders = null;
    let setProvidersCompare = null;
    switch (summaryType) {
      case ProviderSetVersionSummaryType.BySpecCode: {
        setProviders = setProvidersBySpecialty;
        setProvidersCompare = setProvidersBySpecialtyCompare;
        setActiveAccordion(summaryType);
        break;
      }
      case ProviderSetVersionSummaryType.ByState: {
        setProviders = setProvidersByState;
        setProvidersCompare = setProvidersByStateCompare;
        setActiveAccordion(summaryType);
        break;
      }
      case ProviderSetVersionSummaryType.ByGeocode: {
        setProviders = setProvidersByGeocodeResult;
        setProvidersCompare = setProvidersByGeocodeResultCompare;
        setActiveAccordion(summaryType);
        break;
      }
      default: {
        return;
      }
    }

    setProviders(await providerSetService.getProviderSetVersionSummaryCount(props.providerSetId, props.version.providerSetVersionId.toString(), summaryType));

    if (!!props.compareVersion) {
      setProvidersCompare(await providerSetService.getProviderSetVersionSummaryCount(props.providerSetId, props.compareVersion.providerSetVersionId.toString(), summaryType));
    } else {
      setProvidersCompare(null);
    }

    setLoading(false);
  }

  return (
    <AccordionContainer>
      <Accordion title={<>{filesOutlineIcon}Providers by Specialty</>} defaultOpen={activeAccordion === ProviderSetVersionSummaryType.BySpecCode} onPanelWillOpen={() => fetchSummaryCount(ProviderSetVersionSummaryType.BySpecCode)} testId="summary-info__providers-by-specialty-accordion">
        {providersBySpecialtyBody}
      </Accordion>
      <Accordion title={<>{filesOutlineIcon}Providers by State</>} defaultOpen={activeAccordion === ProviderSetVersionSummaryType.ByState} onPanelWillOpen={() => fetchSummaryCount(ProviderSetVersionSummaryType.ByState)} testId="summary-info__providers-by-state-accordion">
        <QESCheckbox label="Show All States" testId="provider-set-detail__show-all-states" onChange={(e) => setShowAllStates(e.target.checked)} checked={showAllStates} />
        {providersByStateBody}
      </Accordion>
      <Accordion title={<>{filesOutlineIcon}Providers by Geocode Default</>} defaultOpen={activeAccordion === ProviderSetVersionSummaryType.ByGeocode} onPanelWillOpen={() => fetchSummaryCount(ProviderSetVersionSummaryType.ByGeocode)} testId="summary-info__providers-by-geocode-result-accordion">
        {providersByGeocodeResultBody}
      </Accordion>
    </AccordionContainer>
  );
}